import React from 'react';
import { toast } from 'sonner';
import { useLanguage } from '../../i18n';

const getEnergyUnit = (system, t) => {
  switch (system) {
    case 'oel': return t('energyUnitOel');
    case 'gas': return t('energyUnitGas');
    case 'elektro': return t('energyUnitElektro');
    case 'holz': return t('energyUnitHolz');
    case 'luft_wp': return t('energyUnitLuftWp');
    default: return t('energyUnitDefault');
  }
};

const getEnergyPlaceholder = (system) => {
  switch (system) {
    case 'oel': return 'z.B. 2500';
    case 'gas': return 'z.B. 2000';
    case 'elektro': return 'z.B. 15000';
    case 'holz': return 'z.B. 10';
    case 'luft_wp': return 'z.B. 15000';
    default: return 'z.B. 2000';
  }
};

const Step3_Demand = ({ 
  formData, 
  onInputChange,
  onFormDataChange,
  onBack, 
  onNext,
  onLoadInfo
}) => {
  const { t } = useLanguage();

  const handleNext = () => {
    if (formData.project_type === 'neubau') {
      if (!formData.energy_consumption_kwh) { toast.error(t('valEnergyRequired')); return; }
      if (formData.energy_includes_hot_water === null) { toast.error(t('valEnergyInclHwRequired')); return; }
      if (formData.energy_includes_hot_water === false) {
        if (!formData.persons) { toast.error(t('valPersonsRequired')); return; }
        if (formData.heating_hot_water === null) { toast.error(t('valHotWaterRequired')); return; }
      }
    }
    if (formData.project_type === 'tausch') {
      if (!formData.prev_system) { toast.error(t('valPrevSystemRequired')); return; }
      if (!formData.energy_consumption_kwh) { toast.error(t('valEnergyRequired')); return; }
      if (formData.energy_includes_hot_water === null) { toast.error(t('valEnergyInclHwRequired')); return; }
      if (formData.energy_includes_hot_water === false) {
        if (!formData.persons) { toast.error(t('valPersonsRequired')); return; }
        if (formData.heating_hot_water === null) { toast.error(t('valHotWaterRequired')); return; }
      }
    }
    onNext();
  };

  const HotWaterPersonsBlock = ({ idPrefix }) => (
    <>
      <div className="form-group">
        <label className="form-label">{t('personsLabel')}</label>
        <input type="number" name="persons" value={formData.persons} onChange={onInputChange} className="form-input" placeholder={t('personsPlaceholder')} min="1" max="20" data-testid="persons-input" />
      </div>
      {formData.persons && (
        <div className="form-group">
          <label className="form-label">{t('hotWaterQuestion')}</label>
          <div className="radio-group">
            <div className="radio-option">
              <input type="radio" name="heating_hot_water" value="true" checked={formData.heating_hot_water === true} onChange={() => onFormDataChange({ ...formData, heating_hot_water: true })} id={`${idPrefix}-hot-water-yes`} data-testid={`${idPrefix}-hot-water-yes`} />
              <label htmlFor={`${idPrefix}-hot-water-yes`} className="radio-label">{t('yes')}</label>
            </div>
            <div className="radio-option">
              <input type="radio" name="heating_hot_water" value="false" checked={formData.heating_hot_water === false} onChange={() => onFormDataChange({ ...formData, heating_hot_water: false })} id={`${idPrefix}-hot-water-no`} data-testid={`${idPrefix}-hot-water-no`} />
              <label htmlFor={`${idPrefix}-hot-water-no`} className="radio-label">{t('no')}</label>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const EnergyIncludesHwBlock = ({ idPrefix }) => (
    <div className="form-group">
      <label className="form-label">{t('energyIncludesHotWater')}</label>
      <div className="radio-group">
        <div className="radio-option">
          <input type="radio" name="energy_includes_hot_water" value="true" checked={formData.energy_includes_hot_water === true} onChange={() => onFormDataChange({ ...formData, energy_includes_hot_water: true, persons: '', heating_hot_water: true })} id={`${idPrefix}-energy-includes-hw-yes`} data-testid={`${idPrefix}-energy-includes-hw-yes`} />
          <label htmlFor={`${idPrefix}-energy-includes-hw-yes`} className="radio-label">{t('yes')}</label>
        </div>
        <div className="radio-option">
          <input type="radio" name="energy_includes_hot_water" value="false" checked={formData.energy_includes_hot_water === false} onChange={() => onFormDataChange({ ...formData, energy_includes_hot_water: false, heating_hot_water: null })} id={`${idPrefix}-energy-includes-hw-no`} data-testid={`${idPrefix}-energy-includes-hw-no`} />
          <label htmlFor={`${idPrefix}-energy-includes-hw-no`} className="radio-label">{t('no')}</label>
        </div>
      </div>
    </div>
  );

  return (
    <div className="step-content" data-testid="step-3">
      <h2 className="step-title">{t('step3Title')}</h2>
      <p className="step-description">{t('step3Desc')}</p>
      
      {formData.project_type === 'neubau' && (
        <>
          <div className="form-group">
            <label className="form-label">{t('energyConsumptionLabel')}</label>
            <input type="number" name="energy_consumption_kwh" value={formData.energy_consumption_kwh} onChange={onInputChange} className="form-input" placeholder={t('energyConsumptionPlaceholder')} min="1000" max="500000" data-testid="energy-consumption-input" />
          </div>
          {formData.energy_consumption_kwh && <EnergyIncludesHwBlock idPrefix="neubau" />}
          {formData.energy_includes_hot_water === false && <HotWaterPersonsBlock idPrefix="neubau" />}
          <div className="form-group">
            <label className="form-label">{t('areaLabel')} <span style={{ fontSize: '0.875rem', color: '#666' }}>({t('optional')})</span></label>
            <input type="number" name="area_m2" value={formData.area_m2} onChange={onInputChange} className="form-input" placeholder={t('areaPlaceholder')} min="20" max="10000" data-testid="area-input" />
          </div>
          <div className="form-group">
            <label className="form-label">{t('altitudeLabel')} <span style={{ fontSize: '0.875rem', color: '#666' }}>({t('optional')})</span></label>
            <input type="number" name="altitude_m" value={formData.altitude_m} onChange={onInputChange} className="form-input" placeholder={t('altitudePlaceholder')} min="0" max="4000" data-testid="altitude-input" />
          </div>
          <div className="form-group">
            <label className="form-label">{t('freeCoolingLabel')}</label>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" name="free_cooling" value="true" checked={formData.free_cooling === true} onChange={() => onFormDataChange({ ...formData, free_cooling: true })} id="free-cooling-yes" data-testid="free-cooling-yes" />
                <label htmlFor="free-cooling-yes" className="radio-label">{t('yes')}</label>
              </div>
              <div className="radio-option">
                <input type="radio" name="free_cooling" value="false" checked={formData.free_cooling === false} onChange={() => onFormDataChange({ ...formData, free_cooling: false })} id="free-cooling-no" data-testid="free-cooling-no" />
                <label htmlFor="free-cooling-no" className="radio-label">{t('no')}</label>
              </div>
            </div>
          </div>
        </>
      )}
      
      {formData.project_type === 'tausch' && (
        <>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>{t('prevSystemLabel')}</span>
              <button type="button" onClick={onLoadInfo} style={{ background: 'none', border: 'none', color: '#4CAF50', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline', padding: '0', display: 'flex', alignItems: 'center', gap: '0.25rem' }} data-testid="heating-system-info-link">
                {t('moreInfo')}
              </button>
            </label>
            <select name="prev_system" value={formData.prev_system} onChange={(e) => onFormDataChange({ ...formData, prev_system: e.target.value, energy_consumption_kwh: '' })} className="form-select" data-testid="prev-system-select">
              <option value="">{t('prevSystemPlaceholder')}</option>
              <option value="oel">{t('prevSystemOel')}</option>
              <option value="gas">{t('prevSystemGas')}</option>
              <option value="elektro">{t('prevSystemElektro')}</option>
              <option value="holz">{t('prevSystemHolz')}</option>
              <option value="luft_wp">{t('prevSystemLuftWp')}</option>
            </select>
          </div>
          {formData.prev_system && (
            <div className="form-group">
              <label className="form-label">{t('energyConsumptionDynamicLabel')} ({getEnergyUnit(formData.prev_system, t)})*</label>
              <input type="number" name="energy_consumption_kwh" value={formData.energy_consumption_kwh} onChange={onInputChange} className="form-input" placeholder={getEnergyPlaceholder(formData.prev_system)} min="1" max="500000" data-testid="energy-consumption-input" />
            </div>
          )}
          {formData.prev_system && formData.energy_consumption_kwh && <EnergyIncludesHwBlock idPrefix="energy" />}
          {formData.energy_includes_hot_water === false && <HotWaterPersonsBlock idPrefix="tausch" />}
          <div className="form-group">
            <label className="form-label">{t('areaLabel')} <span style={{ fontSize: '0.875rem', color: '#666' }}>({t('optional')})</span></label>
            <input type="number" name="area_m2" value={formData.area_m2} onChange={onInputChange} className="form-input" placeholder={t('areaPlaceholder')} min="20" max="2000" data-testid="tausch-area-input" />
          </div>
          <div className="form-group">
            <label className="form-label">{t('altitudeLabel')} <span style={{ fontSize: '0.875rem', color: '#666' }}>({t('optional')})</span></label>
            <input type="number" name="altitude_m" value={formData.altitude_m} onChange={onInputChange} className="form-input" placeholder={t('altitudePlaceholder')} min="0" max="4000" data-testid="altitude-input" />
          </div>
        </>
      )}
      
      <div className="button-group">
        <button onClick={onBack} className="btn-secondary" data-testid="step-3-back-btn">{t('back')}</button>
        <button onClick={handleNext} className="btn-primary" data-testid="step-3-next-btn">{t('next')}</button>
      </div>
    </div>
  );
};

export default Step3_Demand;
